import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LEVELS, levelLabel } from "@/lib/levels";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Pencil, Loader2, GraduationCap, Eye, Trash2 } from "lucide-react";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";

const EMPTY = {
  full_name: "",
  enrollment_date: "",
  level: "",
  age: "",
  gender: "",
  session: "",
  guardian_name: "",
  guardian_phone: "",
  guardian_email: "",
  guardian_phone_2: "",
  guardian_email_2: "",
  total_fees: "",
  paid_fees: "",
  payment_date: "",
  photo_consent: false,
  medical_notes: "",
  admin_notes: "",
  assigned_teacher_ids: [],
  active: true,
};

function StudentDialog({ open, onOpenChange, student, teachers, onSaved }) {
  const isEdit = !!student;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        student
          ? {
              full_name: student.full_name || "",
              enrollment_date: student.enrollment_date || "",
              level: student.level || "",
              age: student.age ?? "",
              gender: student.gender || "",
              session: student.session || "",
              guardian_name: student.guardian_name || "",
              guardian_phone: student.guardian_phone || "",
              guardian_email: student.guardian_email || "",
              guardian_phone_2: student.guardian_phone_2 || "",
              guardian_email_2: student.guardian_email_2 || "",
              total_fees: student.total_fees ?? "",
              paid_fees: student.paid_fees ?? "",
              payment_date: student.payment_date || "",
              photo_consent: student.photo_consent ?? false,
              medical_notes: student.medical_notes || "",
              admin_notes: student.admin_notes || "",
              assigned_teacher_ids: student.assigned_teacher_ids || [],
              active: student.active ?? true,
            }
          : EMPTY
      );
    }
  }, [open, student]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleTeacher = (id) =>
    setForm((f) => ({
      ...f,
      assigned_teacher_ids: f.assigned_teacher_ids.includes(id)
        ? f.assigned_teacher_ids.filter((x) => x !== id)
        : [...f.assigned_teacher_ids, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
      const payload = {
        ...form,
        enrollment_date: form.enrollment_date || null,
        age: form.age === "" || form.age == null ? null : parseInt(form.age, 10),
        total_fees: numOrNull(form.total_fees),
        paid_fees: numOrNull(form.paid_fees),
        payment_date: form.payment_date || null,
      };
      if (isEdit) {
        await api.put(`/admin/students/${student.id}`, payload);
        toast.success("Student updated");
      } else {
        delete payload.active;
        await api.post("/admin/students", payload);
        toast.success("Student created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const activeTeachers = teachers.filter((t) => t.active || form.assigned_teacher_ids.includes(t.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit student" : "Add student"}
          </DialogTitle>
          <DialogDescription>
            Student details, guardian contact, and teacher assignment.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="student-form">
          <div className="space-y-2">
            <Label htmlFor="s-name">Full name</Label>
            <Input
              id="s-name"
              data-testid="student-fullname-input"
              className="h-11"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="s-age">Age</Label>
              <Input
                id="s-age"
                data-testid="student-age-input"
                className="h-11"
                type="number"
                min="0"
                value={form.age}
                onChange={(e) => set("age", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-enroll">Enrollment date</Label>
              <Input
                id="s-enroll"
                data-testid="student-enrollment-input"
                className="h-11"
                type="date"
                value={form.enrollment_date || ""}
                onChange={(e) => set("enrollment_date", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="s-level">Level</Label>
              <Select value={form.level || "none"} onValueChange={(v) => set("level", v === "none" ? "" : v)}>
                <SelectTrigger id="s-level" className="h-11" data-testid="student-level-select">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value} data-testid={`student-level-${l.value}`}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-gender">Gender</Label>
              <Select value={form.gender || "none"} onValueChange={(v) => set("gender", v === "none" ? "" : v)}>
                <SelectTrigger id="s-gender" className="h-11" data-testid="student-gender-select">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  <SelectItem value="Male" data-testid="student-gender-male">Male</SelectItem>
                  <SelectItem value="Female" data-testid="student-gender-female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-session">Session</Label>
              <Select value={form.session || "none"} onValueChange={(v) => set("session", v === "none" ? "" : v)}>
                <SelectTrigger id="s-session" className="h-11" data-testid="student-session-select">
                  <SelectValue placeholder="Session" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  <SelectItem value="Early" data-testid="student-session-early">Early</SelectItem>
                  <SelectItem value="Late" data-testid="student-session-late">Late</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2">
              <Label htmlFor="s-gname">Guardian name</Label>
              <Input
                id="s-gname"
                data-testid="student-guardian-name-input"
                className="h-11"
                value={form.guardian_name}
                onChange={(e) => set("guardian_name", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="s-gphone">Guardian phone</Label>
                <Input
                  id="s-gphone"
                  data-testid="student-guardian-phone-input"
                  className="h-11"
                  value={form.guardian_phone}
                  onChange={(e) => set("guardian_phone", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-gemail">Guardian email</Label>
                <Input
                  id="s-gemail"
                  data-testid="student-guardian-email-input"
                  className="h-11"
                  type="email"
                  value={form.guardian_email}
                  onChange={(e) => set("guardian_email", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="s-gphone2">Guardian phone 2</Label>
                <Input
                  id="s-gphone2"
                  data-testid="student-guardian-phone2-input"
                  className="h-11"
                  value={form.guardian_phone_2}
                  onChange={(e) => set("guardian_phone_2", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-gemail2">Guardian email 2</Label>
                <Input
                  id="s-gemail2"
                  data-testid="student-guardian-email2-input"
                  className="h-11"
                  type="email"
                  value={form.guardian_email_2}
                  onChange={(e) => set("guardian_email_2", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assigned teachers</Label>
            <div className="rounded-lg border border-border max-h-44 overflow-y-auto divide-y divide-border" data-testid="teacher-assign-list">
              {activeTeachers.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No teachers available.</p>
              ) : (
                activeTeachers.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-accent"
                  >
                    <Checkbox
                      checked={form.assigned_teacher_ids.includes(t.id)}
                      onCheckedChange={() => toggleTeacher(t.id)}
                      data-testid={`assign-teacher-${t.id}`}
                    />
                    <span className="text-sm">
                      {t.full_name}
                      {!t.active && <span className="text-muted-foreground"> (inactive)</span>}
                    </span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Select one or more. Editing this reassigns (transfers) the student.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="s-total-fees">Total fees (admin-only)</Label>
              <Input
                id="s-total-fees"
                data-testid="student-total-fees-input"
                className="h-11"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.total_fees}
                onChange={(e) => set("total_fees", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-paid-fees">Paid fees (admin-only)</Label>
              <Input
                id="s-paid-fees"
                data-testid="student-paid-fees-input"
                className="h-11"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.paid_fees}
                onChange={(e) => set("paid_fees", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="s-payment-date">Payment date (admin-only)</Label>
            <Input
              id="s-payment-date"
              data-testid="student-payment-date-input"
              className="h-11"
              type="date"
              value={form.payment_date || ""}
              onChange={(e) => set("payment_date", e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium">Photo consent</p>
              <p className="text-xs text-muted-foreground">Consent to use photos on social media.</p>
            </div>
            <Switch
              checked={form.photo_consent}
              onCheckedChange={(v) => set("photo_consent", v)}
              data-testid="student-photo-consent-switch"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="s-medical">Medical notes (admin-only)</Label>
            <Textarea
              id="s-medical"
              data-testid="student-medical-notes-input"
              value={form.medical_notes}
              onChange={(e) => set("medical_notes", e.target.value)}
              placeholder="Allergies, conditions — not visible to teachers"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="s-admin-notes">Admin notes (admin-only)</Label>
            <Textarea
              id="s-admin-notes"
              data-testid="student-admin-notes-input"
              value={form.admin_notes}
              onChange={(e) => set("admin_notes", e.target.value)}
              placeholder="Internal notes — not visible to teachers"
              rows={2}
            />
          </div>

          {isEdit && (
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium">Student active</p>
                <p className="text-xs text-muted-foreground">Inactive students are archived.</p>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => set("active", v)}
                data-testid="student-active-switch"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={saving} className="h-11" data-testid="student-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDeleteStudent = async () => {
    try {
      const { data } = await api.delete(`/admin/students/${deleteTarget.id}`);
      toast.success(
        `Deleted ${data.student_deleted} — ${data.records_deleted} records, ${data.adjustments_deleted} adjustments removed`
      );
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/students"),
        api.get("/admin/teachers"),
      ]);
      setStudents(s.data);
      setTeachers(t.data);
    } catch (e) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      students.filter(
        (s) =>
          s.full_name.toLowerCase().includes(search.toLowerCase()) &&
          (sessionFilter === "all" || (s.session || "") === sessionFilter)
      ),
    [students, search, sessionFilter]
  );

  return (
    <div className="max-w-6xl" data-testid="students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
            Students
          </h1>
          <p className="mt-1 text-muted-foreground">Manage students and teacher assignments.</p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          className="h-11"
          data-testid="add-student-button"
        >
          <Plus className="h-4 w-4 mr-2" /> Add student
        </Button>
      </div>

      <div className="relative mt-6 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="student-search-input"
            className="h-11 pl-9"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={sessionFilter} onValueChange={setSessionFilter}>
          <SelectTrigger className="h-11 w-40" data-testid="student-session-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sessions</SelectItem>
            <SelectItem value="Early">Early</SelectItem>
            <SelectItem value="Late">Late</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="students-empty">
            <GraduationCap className="h-8 w-8 mx-auto mb-3 opacity-40" />
            {students.length === 0 ? "No students yet. Add your first student." : "No matches."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Guardian</TableHead>
                  <TableHead>Teachers</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/admin/students/${s.id}`}
                        className="hover:text-primary hover:underline transition-colors"
                        data-testid={`student-name-link-${s.id}`}
                      >
                        {s.full_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {levelLabel(s.level) || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.session ? (
                        <Badge variant="secondary" className="font-normal">{s.session}</Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.guardian_name || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {s.assigned_teachers?.length ? (
                          s.assigned_teachers.map((t) => (
                            <Badge key={t.id} variant="secondary" className="font-normal">
                              {t.full_name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">Unassigned</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {s.active ? (
                        <Badge className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        data-testid={`view-student-${s.id}`}
                      >
                        <Link to={`/admin/students/${s.id}`}>
                          <Eye className="h-4 w-4 mr-1.5" /> View
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(s);
                          setDialogOpen(true);
                        }}
                        data-testid={`edit-student-${s.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(s)}
                        data-testid={`delete-student-${s.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <StudentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        student={editing}
        teachers={teachers}
        onSaved={load}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete student?"
        confirmName={deleteTarget?.full_name}
        impact={
          deleteTarget
            ? `Delete ${deleteTarget.full_name}? This will permanently remove all of this student's daily records, point adjustments, and profile. This cannot be undone.`
            : ""
        }
        onConfirm={confirmDeleteStudent}
      />
    </div>
  );
}
